import React, { useMemo } from "react";
import InlineLink from "~/views/components/content/inline-link";
import { MDXProvider } from "@mdx-js/react";
import { renderNode } from "~/views/components/content/markdown";
import rehypeRaw from "rehype-raw";
import { unified } from "unified";
import remarkParse from "remark-parse";
import remarkGfm from "remark-gfm";
import remarkRehype from "remark-rehype";

type Props = {
  node?: any;
};

const components = {
  h2: ({ children, ...props }: any) => (
    <h2
      className="text-xl sm:text-2xl font-bold text-neutral-30 mt-8 mb-4 leading-tight"
      {...props}>
      {children}
    </h2>
  ),

  h3: ({ children, ...props }: any) => (
    <h3 className="py-3 sm:py-4 dashed-t flex gap-2 sm:gap-3 items-start">
      <span className="text-cyan text-base sm:text-2xl font-bold leading-[28px] sm:leading-[32px]">
        Q
      </span>
      <span className="text-neutral-30 font-semibold leading-[28px] sm:leading-[32px] text-xs sm:text-base">
        {children}
      </span>
    </h3>
  ),

  h4: ({ children, ...props }: any) => (
    <h4
      className="text-base sm:text-lg font-bold text-neutral-30 mt-4 mb-2 leading-tight"
      {...props}>
      {children}
    </h4>
  ),

  h5: ({ children, ...props }: any) => (
    <h5
      className="text-sm sm:text-base font-bold text-neutral-30 mt-3 mb-2 leading-tight"
      {...props}>
      {children}
    </h5>
  ),

  // Paragraphs
  p: ({ children, ...props }: any) => (
    <p className="pt-3 sm:pt-4 pb-7 sm:pb-8 dashed-t flex gap-2 sm:gap-3 items-start">
      <span className="text-cyan text-base sm:text-2xl font-bold leading-[28px] sm:leading-[32px]">
        A
      </span>
      <span className="text-neutral-30 font-regula leading-[28px] sm:leading-[32px] text-xs sm:text-base">
        {children}
      </span>
    </p>
  ),

  // Lists
  ul: ({ children, ...props }: any) => (
    <ul className="marker:text-cyan list-outside list-disc" {...props}>
      {children}
    </ul>
  ),

  ol: ({ children, ...props }: any) => (
    <ol className="list-decimal list-outside marker:text-cyan" {...props}>
      {children}
    </ol>
  ),

  li: ({ children, ...props }: any) => (
    <li
      className="richtext-list-item ml-4 py-3 sm:py-4 font-medium text-sm sm:text-base leading-[30px] sm:leading-[32px] text-neutral-30"
      {...props}>
      {children}
    </li>
  ),

  // Blockquotes
  blockquote: ({ children, ...props }: any) => (
    <blockquote
      className="border-l-4 border-cyan pl-4 my-4 italic text-neutral-30 leading-8"
      {...props}>
      {children}
    </blockquote>
  ),

  // Tables
  table: ({ children, ...props }: any) => (
    <div className="overflow-x-auto my-4">
      <table
        className="min-w-full border-collapse border border-neutral-20"
        {...props}>
        {children}
      </table>
    </div>
  ),

  tableRow: ({ children, ...props }: any) => (
    <tr className="border-b border-neutral-20" {...props}>
      {children}
    </tr>
  ),

  tableCell: ({ children, ...props }: any) => (
    <td
      className="border border-neutral-20 px-4 py-2 text-neutral-30 text-sm sm:text-base"
      {...props}>
      {children}
    </td>
  ),

  th: ({ children, ...props }: any) => (
    <th
      className="border border-neutral-20 px-4 py-2 bg-neutral-10 font-bold text-neutral-30 text-sm sm:text-base text-left"
      {...props}>
      {children}
    </th>
  ),

  // Text formatting
  strong: ({ children, ...props }: any) => (
    <strong className="font-bold" {...props}>
      {children}
    </strong>
  ),

  em: ({ children, ...props }: any) => (
    <em className="italic font-medium" {...props}>
      {children}
    </em>
  ),

  // Links
  a: ({ children, href, ...props }: any) => (
    <InlineLink to={href}>{children}</InlineLink>
  ),
};

function FAQ({ node }: Props) {
  const { frontmatter, body } = node;

  const file = unified()
    .use(remarkParse)
    .use(remarkGfm)
    .use(remarkRehype, { allowDangerousHtml: true })
    .use(rehypeRaw)
    .parse(body);

  const renderedContent = useMemo(() => {
    return renderNode(file, 0, components);
  }, [file]);

  return (
    <div className="mt-24 mb-16 border-2 border-neutral-90 shadow-neutral rounded-lg px-3 sm:px-10">
      <h2
        id="faq"
        className="pt-4 sm:pt-8 pb-3 sm:pb-6 font-bold text-base sm:text-lg text-center">
        {frontmatter.title}
      </h2>
      <MDXProvider components={components}>{renderedContent}</MDXProvider>
    </div>
  );
}

export default FAQ;
